import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { StorageUtils } from '../Utils/StorageUtils';
import { ApiService } from '../Services/APIService';

const ProtectedRoute = ({ children }) => {
  const [isValidating, setIsValidating] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      const userData = StorageUtils.getUserData();
      
      // If no user data in localStorage, not authenticated
      if (!userData || !userData.token) {
        setIsValidating(false);
        setIsAuthenticated(false);
        return;
      }

      try {
        // Set auth token for the API call
        ApiService.setAuthToken(userData.token);
        
        // Validate token by calling user profile endpoint
        // This will return 401/403 if token is invalid, or network error if backend is down
        await ApiService.get('users/profile');
        
        // If we get here, token is valid and backend is available
        setIsAuthenticated(true);
      } catch (error) {
        // Handle various error cases
        const status = error.response?.status;
        const isNetworkError = !error.response; // No response means network error (backend down)
        const isAuthError = status === 401 || status === 403; // Invalid/expired token
        
        if (isNetworkError || isAuthError) {
          // Clear invalid/expired token data
          console.log('❌ Token validation failed:', isNetworkError ? 'Backend unreachable' : 'Invalid token');
          StorageUtils.clearAllUserCache();
          setIsAuthenticated(false);
        } else {
          // Other errors (500, etc.) - treat as authentication failure to be safe
          console.error('❌ Unexpected error validating token:', error);
          StorageUtils.clearAllUserCache();
          setIsAuthenticated(false);
        }
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, []); // Run once on mount

  // Show loading state while validating
  if (isValidating) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div>Validating authentication...</div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/auth?type=login" />;
  }

  // Render protected content if authenticated
  return children;
};

export default ProtectedRoute;
